import styles from "./client-profile-access-tab.module.css";

import classNames from "classnames";
import {
  AccessAreaType,
  ClientProfileAccessSettings,
  ClientProfileDetails,
} from "../../store/types";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import TimeAlertRadio from "../../cells/time-alerts-radio";
import { Edit } from "../../../../components/svg";
import { AuthStore, TransitionStore } from "../../../../store";
import { CreateConfirmationDialog } from "../../../../utils/ConfirmationDialog";
import { ReactComponent as WarningIcon } from "../../svg/warning.svg";
import { ChangeEvent } from "react";
import { SwitchInput } from "../../../../components/form-inputs";
import { useNavigate } from "react-router-dom";
import { ClientProfileStore } from "../../store/store";

interface ClientProfileAccessTabProps {
  clientAccess: ClientProfileAccessSettings;
  clientDetails: ClientProfileDetails;
  authStore: AuthStore;
  transitionStore: TransitionStore;
  toggleAccessStatus(): void;
  switchAccessTimeStatus(): void;
  isAccessInformationValid: boolean;
  clientProfileStore?: ClientProfileStore;
}

function ClientProfileAccessTab(props: ClientProfileAccessTabProps) {
  const { t } = useTranslation("client-profile");
  const {
    clientAccess,
    clientDetails,
    isAccessInformationValid,
    authStore,
    transitionStore,
    toggleAccessStatus,
    switchAccessTimeStatus,
    clientProfileStore,
  } = props;

  const history = useNavigate();

  const render = () => {
    if (!clientProfileStore?.clientProfileDetails.hasTelecareCapability)
      return notSupported();

    if (!isAccessInformationValid) return showCannotRetrieveData();

    if (clientAccess.clientProfileAssignmentAreas.length < 1) {
      return showNotAvailableAccessTab();
    }
    return (
      <div className={styles["tabs-container"]}>
        <li className={`${styles["item"]} ${styles["odd-tab"]}`}>
          <div className={styles["title"]}>
            {t("Access inside / outside of the building")}
          </div>
        </li>
        <li className={`${styles["item"]} ${styles["even-tab"]}`}>
          {renderAccessToggleButton()}
          {renderTimeIntervalSwitches()}
          {renderAccessAreasInformation()}
          {renderEditAccessSettingsButtons()}
        </li>
      </div>
    );
  };

  const renderAccessToggleButton = () => {
    const { dialogTitle, dialogContent, okButtonText, cancelButtonText } =
      prepareToggleDialogInformation();

    const confirmationDialogData = CreateConfirmationDialog(
      dialogTitle,
      dialogContent,
      okButtonText,
      cancelButtonText,
      false,
      () => {
        clientDetails.isAccessProfileEnabled =
          !clientDetails.isAccessProfileEnabled;
        toggleAccessStatus();
      }
    );

    return (
      <div className={`${styles["toggle-button"]} ${styles["col-1"]}`}>
        <div className={styles["row-1"]}>
          <div className={styles["switch-container"]}>
            <SwitchInput
              checked={clientDetails.isAccessProfileEnabled}
              disabled={!authStore.hasAdminRights()}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                transitionStore.showGlobalDialog(confirmationDialogData);
              }}
            />
          </div>
        </div>
        <div className={styles["row-2"]} />
      </div>
    );
  };

  const prepareToggleDialogInformation = () => {
    const toggleStatus = !clientDetails.isAccessProfileEnabled
      ? t("common:On")
      : t("common:Off");
    const action = !clientDetails.isAccessProfileEnabled
      ? t("common:Start")
      : t("common:Stop");

    const dialogTitle = t("AccessToggleDialogTitle", {
      toggleStatus: toggleStatus,
    });
    const okButtonText = t("AccessToggleDialogConfirmation", {
      toggleStatus: toggleStatus,
    });
    const cancelButtonText = t("common:Cancel");
    const dialogContent = `${t("AccessToggleDialogAction", { action: action })}
        ${t("AccessToggleDialogQuestion", {
          toggleStatus: toggleStatus,
          clientName: clientDetails.clientName,
        })}`;
    return { dialogTitle, okButtonText, cancelButtonText, dialogContent };
  };

  const renderTimeIntervalSwitches = () => {
    const dayAccess = clientAccess.getDaytimeInterval();
    const nightAccess = clientAccess.getNighttimeInterval();

    const intervalTranslationKey: { [index: string]: string } = {
      Daytime: "Day time access",
      Nighttime: "Night time access",
      None: "Custom access",
    };

    const assigments =
      clientProfileStore?.clientProfileAccessDetails
        .clientProfileAssignmentAreas;

    const accessItems = assigments?.map((assigment) => {
      const accessType =
        clientProfileStore?.clientProfileAccessDetails.getTypeOfAccess(
          assigment
        );

      let startTime = null;
      let endTime = null;

      if (accessType === AccessAreaType.DAYTIME) {
        startTime = formatHour(dayAccess.accessAreaStartTime);
        endTime = formatHour(nightAccess.accessAreaStartTime);
      } else if (accessType === AccessAreaType.NIGHTIME) {
        startTime = formatHour(nightAccess.accessAreaStartTime);
        endTime = formatHour(dayAccess.accessAreaStartTime);
      }

      const { dialogTitle, dialogContent, okButtonText, cancelButtonText } =
        prepareSwitchDialogInformation(accessType!);

      const confirmationDialogDataDay = CreateConfirmationDialog(
        dialogTitle,
        dialogContent,
        okButtonText,
        cancelButtonText,
        false,
        () => {
          changeAccessStatus(accessType!);
        }
      );

      return (
        <div
          className={classNames([styles["row-1"]], {
            [styles["disabled"]]: !clientDetails.isAccessProfileEnabled,
          })}
        >
          <TimeAlertRadio
            title={t(intervalTranslationKey[accessType!])}
            checked={assigment.isActive}
            disabledInteraction={assigment.isActive}
            onChange={(event: { target: { value: any } }) => {
              transitionStore.showGlobalDialog(confirmationDialogDataDay);
            }}
            startTime={startTime}
            endTime={endTime}
          />
        </div>
      );
    });

    return (
      <div className={`${styles["text"]} ${styles["col-2"]}`}>
        {accessItems}
      </div>
    );
  };

  const prepareSwitchDialogInformation = (
    selectedAccessType: AccessAreaType
  ) => {
    const dayAccess = clientAccess.getDaytimeInterval();
    const nightAccess = clientAccess.getNighttimeInterval();

    let selectedPeriod = t("Day");
    let endingPeriod: string | null = t("Night");
    let endHour: string | null = nightAccess.accessAreaStartTime;

    if (selectedAccessType === AccessAreaType.NIGHTIME) {
      selectedPeriod = t("Night");
      endingPeriod = t("Day");
      endHour = dayAccess.accessAreaStartTime;
    } else if (selectedAccessType === AccessAreaType.NONE) {
      selectedPeriod = `${t("Custom")} `;
      endingPeriod = null;
      endHour = null;
    }

    const clientName = clientDetails.clientName;

    const dialogTitle = t("client-profile:AccessSwitchDialogTitle", {
      selectedPeriod: selectedPeriod,
    });

    const okButtonText = t("client-profile:AccessSwitchDialogConfirmation", {
      selectedPeriod: selectedPeriod,
    });
    const cancelButtonText = t("common:Cancel");

    let dialogContent = `${t("client-profile:AccessSwitchDialogActionCustom", {
      switchStatus: selectedPeriod,
    })} 
                            ${t("client-profile:AccessSwitchDialogQuestion", {
                              switchStatus: selectedPeriod,
                              clientName: clientName,
                            })}`;

    if (endHour) {
      dialogContent = `${t("client-profile:AccessSwitchDialogAction", {
        switchStatus: selectedPeriod,
        endHour: formatHour(endHour),
        endingPeriod: endingPeriod,
      })} 
                            ${t("client-profile:AccessSwitchDialogQuestion", {
                              switchStatus: selectedPeriod,
                              clientName: clientName,
                            })}`;
    }

    return { dialogTitle, dialogContent, okButtonText, cancelButtonText };
  };

  const renderAccessAreasInformation = () => {
    const dayArea = getAreaName(
      clientAccess.getDaytimeInterval().accessLocationId,
      clientAccess.getDaytimeInterval().isAccessGroupToggleActive
    );
    const nightArea = getAreaName(
      clientAccess.getNighttimeInterval().accessLocationId,
      clientAccess.getNighttimeInterval().isAccessGroupToggleActive
    );

    const customArea = getAreaName(
      clientAccess.getCustomInterval().accessLocationId,
      clientAccess.getCustomInterval().isAccessGroupToggleActive
    );

    const items = [dayArea, nightArea, customArea].map((item) => {
      return (
        <div className={`${styles["row-1"]} ${styles["text"]}`}>{t(item)}</div>
      );
    });

    return (
      <div
        className={classNames({
          [styles["col-3"]]: clientDetails.isAccessProfileEnabled,
          [`${styles["col-3"]} ${styles["disabled"]}`]:
            !clientDetails.isAccessProfileEnabled,
        })}
      >
        {items}
      </div>
    );
  };

  const renderEditAccessSettingsButtons = () => {
    const items = [
      {
        text: "Edit day time access",
        type: AccessAreaType.DAYTIME,
      },
      {
        text: "Edit night time access",
        type: AccessAreaType.NIGHTIME,
      },
      {
        text: "Edit custom time access",
        type: AccessAreaType.NONE,
      },
    ].map((item) => {
      return (
        <div className={`${styles["row-1"]} ${styles["edit-text"]}`}>
          <Edit className={styles["edit-day-time-icon"]} />
          <div
            className={styles["edit-day-time-text"]}
            onClick={() => {
              history(
                "/client-profile/access-settings/" + clientDetails.clientId,
                { state: { type: item.type } }
              );
            }}
          >
            {t(item.text)}
          </div>
        </div>
      );
    });

    return (
      <div className={styles["col-4"]}>
        {authStore.hasAdminRights() && <>{items}</>}
      </div>
    );
  };

  const getAreaName = (areaId: number, isActive: boolean) => {
    if (!isActive) {
      return "Disabled";
    }
    const result = clientAccess.clientProfileAccessLocations.find(
      (l) => l.accessId === areaId
    );

    if (result === undefined) return "";

    return result!.name;
  };

  const showNotAvailableAccessTab = () => {
    return (
      <div className={styles["tabs-container"]}>
        <li className={`${styles["item"]} ${styles["odd-tab"]}`}>
          <div className={styles["title"]}>
            {t("Access inside / outside of the building")}
          </div>
        </li>
        <li className={`${styles["item"]} ${styles["even-tab"]}`}>
          <div className={styles["text"]}>{t("Not available")}</div>
        </li>
      </div>
    );
  };

  const showCannotRetrieveData = () => {
    return (
      <div className={styles["tabs-container"]}>
        <li className={`${styles["item"]} ${styles["odd-tab"]}`}>
          <div className={styles["title"]}>
            {t("Access inside / outside of the building")}
          </div>
        </li>
        <li className={`${styles["item"]} ${styles["even-tab"]}`}>
          <div className={`${styles["col-1"]} ${styles["critical"]}`}>
            <WarningIcon width="40px" height="40px" />
          </div>
          <div className={`${styles["col-234"]} ${styles["text"]}`}>
            {t("BuildingAccessInformationNotAvailable")}
          </div>
          <div className={styles["col-4"]} />
        </li>
      </div>
    );
  };

  const notSupported = () => {
    return (
      <div className={styles["tabs-container"]}>
        <li className={`${styles["item"]} ${styles["odd-tab"]}`}>
          <div className={styles["title"]}>
            {t("Access inside / outside of the building")}
          </div>
        </li>
        <li className={`${styles["item"]} ${styles["even-tab"]}`}>
          <div className={styles["col-1"]} />
          <div className={`${styles["col-234"]} ${styles["text"]}`}>
            {t("NotSupported")}
          </div>
          <div className={styles["col-4"]} />
        </li>
      </div>
    );
  };

  const changeAccessStatus = (value: AccessAreaType) => {
    switch (value) {
      case AccessAreaType.DAYTIME:
        clientDetails.activeAccessPeriodId =
          clientAccess.getDaytimeInterval().accessGroupId;
        break;
      case AccessAreaType.NIGHTIME:
        clientDetails.activeAccessPeriodId =
          clientAccess.getNighttimeInterval().accessGroupId;
        break;
      case AccessAreaType.NONE:
        clientDetails.activeAccessPeriodId =
          clientAccess.getCustomInterval().accessGroupId;
        break;
      default:
        break;
    }

    switchAccessTimeStatus();
  };

  const formatHour = (hour: string) => {
    return hour.length > 5 ? hour.substring(0, 5) : hour;
  };

  return render();
}

export default inject("clientProfileStore")(observer(ClientProfileAccessTab));
